import { FlakinessReport } from '@flakiness/flakiness-report';
import { xxHash, xxHashObject } from '@flakiness/shared/common/utils.js';
import { Query } from '../fql/query.js';
import { Ranges } from '../ranges.js';
import { WireTypes } from '../wireTypes.js';
import { Stats as S } from './stats.js';
import { TestOutcomes as T } from './testOutcomes.js';

export type FilterContext = {
  tests: WireTypes.Test[],
  testsMask: T.TestRanges,
  fql: Query,
  etag: string,
}

/** What it takes to map the tests and environments of a report to their indexes. */
export interface TestLookup {
  epoch(): S.TestsEpoch;
  testIndex(testId: S.TestId): S.TestIndex|undefined;
  envIndex(envId: S.EnvId): S.EnvIndex|undefined;
}

/** The content of `JSONTestsAndEnvs`, with the maps to look tests and environments up. */
export type TestsAndEnvs = {
  epoch: S.TestsEpoch,
  tests: WireTypes.Test[],
  envs: WireTypes.RunEnvironment[],
  testIdToIndex: Map<S.TestId, S.TestIndex>,
  envIdToIndex: Map<S.EnvId, S.EnvIndex>,
}

export function parseTestsAndEnvs(json: S.JSONTestsAndEnvs): TestsAndEnvs {
  const envs = json.envs.map(env => S.jsonEnvToWireEnv(env));
  const envIdToIndex = new Map<S.EnvId, S.EnvIndex>();
  for (let i = 0 as S.EnvIndex; i < envs.length; ++i)
    envIdToIndex.set(envs[i].envId, i);

  const testIdToIndex = new Map<S.TestId, S.TestIndex>();
  const testIndexToTest = new Map<S.TestIndex, WireTypes.Test>();
  const buildTests = (node: S.SuiteTreeNode, titles: string[] = []) => {
    for (const t of node.t ?? []) {
      const filePath = t.fileIdx !== undefined ? json.files[t.fileIdx] : undefined;
      const testId = S.computeTestId({
        file: filePath,
        titles,
      });
      testIdToIndex.set(testId, t.idx);
      testIndexToTest.set(t.idx, {
        filePath: filePath ?? '' as any,
        titles: titles.slice(),
        testId,
      });
    }
    if (!node.c)
      return;
    for (const [title, child] of Object.entries(node.c)) {
      titles.push(title);
      buildTests(child, titles);
      titles.pop();
    }
  }
  buildTests(json.testsRoot);
  const tests = Array.from(testIndexToTest).sort((t1, t2) => t1[0] - t2[0]).map(t => t[1]);
  return { epoch: json.epoch, tests, envs, testIdToIndex, envIdToIndex };
}

/** The number of tests under `node`. Unlike `parseTestsAndEnvs`, it builds nothing. */
function countTests(node: S.SuiteTreeNode): number {
  let count = node.t?.length ?? 0;
  if (node.c) {
    for (const title in node.c)
      count += countTests(node.c[title]);
  }
  return count;
}

export function serializeTestsAndEnvs({ epoch, tests, envs }: Pick<TestsAndEnvs, 'epoch'|'tests'|'envs'>): S.JSONTestsAndEnvs {
  const testsRoot: S.SuiteTreeNode = {};
  const fileToFileIdx = new Map<FlakinessReport.GitFilePath, number>();

  for (let idx = 0 as S.TestIndex; idx < tests.length; ++idx) {
    const test = tests[idx];
    let wp = testsRoot;
    for (const title of test.titles) {
      // Null-prototype map: test/suite titles are untrusted strings that may
      // collide with Object.prototype keys ("constructor", "__proto__", ...).
      // The cast restores a concrete type, since Object.create() returns `any`.
      wp.c ??= Object.create(null) as Record<string, S.SuiteTreeNode>;
      wp.c[title] ??= {};
      wp = wp.c[title];
    }

    const jsonTest: S.TestTreeNode = { idx };
    if (test.filePath) {
      jsonTest.fileIdx = fileToFileIdx.get(test.filePath);
      if (jsonTest.fileIdx === undefined) {
        jsonTest.fileIdx = fileToFileIdx.size;
        fileToFileIdx.set(test.filePath, jsonTest.fileIdx);
      }
    }
    wp.t ??= [];
    wp.t.push(jsonTest)
  }

  return {
    envs: envs.map(env => ({
      ...env,
      envId: undefined, // serialize without envId.
    })),
    epoch,
    files: Array.from(fileToFileIdx.keys()),
    testsRoot,
  };
}

/**
 * The tests and environments of a project, each with a stable index. A
 * `TestIndex` never changes once it is created; `TestIndexBuilder` is the one
 * that grows.
 */
export class TestIndex implements TestLookup {
  static etagFilterContext(testIndex: TestIndex, fql: Query) {
    return xxHash([testIndex.etag(), fql.etag()]);
  }

  static deserialize(json: S.JSONTestsAndEnvs = S.createEmptyTests()): TestIndex {
    return new TestIndex(parseTestsAndEnvs(json));
  }

  private _tests: WireTypes.Test[];
  private _envs: WireTypes.RunEnvironment[];
  private _envIdToIndex: Map<S.EnvId, S.EnvIndex>;
  private _testIdToIndex: Map<S.TestId, S.TestIndex>;
  private _testScores?: Int32Array;
  private _etag: string|undefined;
  private _epoch: S.TestsEpoch;

  /** The lists and the maps are not copied: the index owns them from here on. */
  constructor({ epoch, tests, envs, testIdToIndex, envIdToIndex }: TestsAndEnvs) {
    this._epoch = epoch;
    this._tests = tests;
    this._envs = envs;
    this._testIdToIndex = testIdToIndex;
    this._envIdToIndex = envIdToIndex;
  }

  /**
   * Returns this index if `json` describes the same tests and environments.
   * Within an epoch both are only ever appended, so two indexes of one epoch
   * that have as many tests and as many environments are the same index.
   */
  tryToReuse(json: S.JSONTestsAndEnvs): TestIndex|undefined {
    if (json.epoch !== this._epoch || json.envs.length !== this._envs.length)
      return undefined;
    // Counting walks the whole tests tree, so it goes last.
    return countTests(json.testsRoot) === this._tests.length ? this : undefined;
  }

  epoch() {
    return this._epoch;
  }

  etag() {
    if (!this._etag)
      this._etag = xxHashObject({ epoch: this._epoch, tests: this._tests.map(t => t.testId), envs: this._envs });
    return this._etag;
  }

  testScores(): Int32Array {
    if (!this._testScores) {
      // Compute test scores. These are their sorted order.
      // For certain projects, i.e. Kotlin, which has 300k tests, each with VERY long
      // name, this might take ~200ms.
      const testIndexes = new Int32Array(this._tests.length);
      for (let i = 0; i < testIndexes.length; ++i)
        testIndexes[i] = i;

      const testComparator = (idx1: number, idx2: number) => {
        const a = this._tests[idx1];
        const b = this._tests[idx2];
        for (let i = 0; i < Math.min(a.titles.length, b.titles.length); ++i) {
          if (a.titles[i] !== b.titles[i])
            return a.titles[i] < b.titles[i] ? -1 : 1;
        }
        if (a.titles.length !== b.titles.length)
          return a.titles.length - b.titles.length;

        const filePathA = a.filePath ?? '';
        const filePathB = b.filePath ?? '';
        if (filePathA !== filePathB)
          return filePathA < filePathB ? -1 : 1;
        return 0;
      }

      testIndexes.sort(testComparator);
      this._testScores = new Int32Array(this._tests.length);
      let lastScore = 0;
      for (let i = 1; i < this._testScores.length; ++i) {
        const r = testComparator(testIndexes[i - 1], testIndexes[i]);
        lastScore = r === 0 ? lastScore : lastScore + 1;
        this._testScores[testIndexes[i]] = lastScore;
      }
    }
    return this._testScores;
  }

  allTests(): T.TestRanges|undefined {
    return this._tests.length ? [0, this._tests.length - 1] as T.TestRanges : undefined;
  }

  createFilterContext(fql: Query, etag: string = TestIndex.etagFilterContext(this, fql)) {
    let testsMask = [0, this._tests.length - 1] as T.TestRanges;
    if (fql.hasTestFilters()) {
      const indexes: S.TestIndex[] = [];
      for (let tidx = 0 as S.TestIndex; tidx < this._tests.length; ++tidx) {
        if (fql.acceptsTest(this._tests[tidx]))
          indexes.push(tidx);
      }
      testsMask = Ranges.fromSortedList(indexes);
    }

    return {
      tests: this._tests,
      testsMask,
      fql,
      etag,
    };
  }

  testIndex(testId: S.TestId) {
    return this._testIdToIndex.get(testId);
  }

  envIndex(envId: S.EnvId) {
    return this._envIdToIndex.get(envId);
  }

  tests() {
    return this._tests;
  }

  environments() {
    return this._envs;
  }

  countTests() {
    return this._tests.length;
  }

  serialize(): S.JSONTestsAndEnvs {
    return serializeTestsAndEnvs({ epoch: this._epoch, tests: this._tests, envs: this._envs });
  }
}
